/**
 * 権限モデル：RBAC + リソーススコープ（02 §3.2）。
 *
 * permission は "<module>.<resource>.<action>[.<scope>]" 形式。
 *   例: expense.request.read.own / expense.request.read.team / expense.request.read.all
 *       expense.request.approve（スコープなし = 全体）
 * スコープは own < team < all の順に広い。
 */
export type PermissionScope = 'own' | 'team' | 'all';

const SCOPES: readonly PermissionScope[] = ['own', 'team', 'all'];
const SCOPE_RANK: Record<PermissionScope, number> = { own: 1, team: 2, all: 3 };

export interface ParsedPermission {
  base: string; // "expense.request.read"
  scope: PermissionScope | null;
}

export function parsePermission(p: string): ParsedPermission {
  const parts = p.split('.');
  const last = parts[parts.length - 1] as PermissionScope | undefined;
  if (last && (SCOPES as readonly string[]).includes(last) && parts.length >= 4) {
    return { base: parts.slice(0, -1).join('.'), scope: last };
  }
  return { base: p, scope: null };
}

/**
 * 保有権限の集合に対して、要求する権限の「最も広いスコープ」を返す。
 * スコープなしの権限を持っていれば 'all' 相当。持っていなければ null。
 */
export function resolveScope(granted: readonly string[], required: string): PermissionScope | null {
  const req = parsePermission(required);
  let best: PermissionScope | null = null;
  for (const g of granted) {
    if (g === '*') return 'all';
    const parsed = parsePermission(g);
    if (parsed.base !== req.base) {
      // ワイルドカード: "expense.*" / "expense.request.*"
      if (g.endsWith('.*') && req.base.startsWith(g.slice(0, -1))) return 'all';
      continue;
    }
    const scope: PermissionScope = parsed.scope ?? 'all';
    if (best === null || SCOPE_RANK[scope] > SCOPE_RANK[best]) best = scope;
  }
  if (req.scope && best && SCOPE_RANK[best] < SCOPE_RANK[req.scope]) return null;
  return best;
}

export function hasPermission(granted: readonly string[], required: string): boolean {
  return resolveScope(granted, required) !== null;
}

const PERMISSION_RE = /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9_-]*)+$/;

export function isValidPermissionName(p: string): boolean {
  return PERMISSION_RE.test(p);
}
